import {
  Badge,
  Box,
  Flex,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  Button,
  Grid as CGrid,
  IconButton,
  Link as Clink,
  useToast
} from "@chakra-ui/react"
import React, { useState, useEffect } from "react"
import { Input, InputWrapper } from "../composites"
import { useQuery, useQueryClient } from "react-query"
import { fetchProfitLoss, fetchAllProfitLossForCSV } from "../../services/main.service"
import _ from "lodash"
import BlockedLoader from "../composites/blocked-loader"
import Paginator from "../composites/paginator"
import DateRangePicker from "../composites/date-range-picker"
import { SelectSymbols } from "../composites/select-symbols"
import { AiOutlineSearch } from "react-icons/ai"
import { AiOutlineDownload } from "react-icons/ai"
import { app } from "../../config"
import { formatDate } from "../../utils"
import { getBaseURL } from "../../services/api"


function convertJsonToCsv(jsonArray: Record<string, any>[]) {
  if (!jsonArray || jsonArray.length === 0) {
    return '';
  }
  

  const headers = Object.keys(jsonArray[0]);
  

  const csvHeader = headers.join(',');
  

  const csvRows = jsonArray.map(obj => {
    return headers
      .map(header => {
        const value = obj[header];
        
        if (value === null || value === undefined) {
          return '';
        }
        
        if (header === 'Time' && value) {
          return `"${value}"`;
        }
        
        if (typeof value === 'string' && 
            (value.includes(',') || value.includes('"') || value.includes('\n') || value.includes(' '))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        
        return value;
      })
      .join(',');
  });
  
  return [csvHeader, ...csvRows].join('\n');
}

interface DownloadFileParams {
  data: string;
  fileName: string;
  fileType: string;
}

/**
 * Creates and triggers download of a file
 */
const downloadFile = ({ data, fileName, fileType }: DownloadFileParams) => {
  // Create blob with the data
  const blob = new Blob([data], { type: fileType });
  
  // Create download link
  const a = document.createElement('a');
  a.download = fileName;
  a.href = window.URL.createObjectURL(blob);
  
  // Trigger download and clean up
  document.body.appendChild(a); 
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(a.href); 
}

const downloadProfitLossToCSV = (apiResponse: any, columns: string[], fileName: string) => {
  // Check if we have deals data
  if (!apiResponse?.data?.deals || apiResponse.data.deals.length === 0) {
    console.warn("No data available for download");
    return;
  }
  
  // Process in batches to prevent UI freeze
  const batchSize = 500;
  const deals = apiResponse.data.deals;
  const totalDeals = deals.length;
  let processedData: Record<string, any>[] = [];
  
  // Map API field keys to readable column names
  const fieldToColumnMap: Record<string, string> = {
    'd': 'Deal',
    'o': 'Order',
    'pid': 'Position',
    'l': 'Login',
    't': 'Time',
    'ty': 'Type',
    'e': 'Entry',
    's': 'Symbol',
    'v': 'Volume',
    'p': 'Price',
    'sl': 'S / L',
    'tp': 'T / P',
    'r': 'Reason',
    'c': 'Commission',
    'f': 'Fee',
    'pr': 'Profit',
    'de': 'Dealer',
    'co': 'Comment'
  };
  
  // Process data in batches using setTimeout to keep UI responsive
  const processBatch = (startIndex: number) => {
    const endIndex = Math.min(startIndex + batchSize, totalDeals);
    
    for (let i = startIndex; i < endIndex; i++) {
      const deal = deals[i];
      const formattedDeal: Record<string, any> = {};
      
      // Format each column according to the display requirements
      columns.forEach(column => {
        if (column === 'Name') {
          // Special handling for Name which combines first, last and middle names
          formattedDeal[column] = `${deal.firstn || ''} ${deal.lastn || ''} ${deal.middlen || ''}`.trim();
        } else {
          // Map API field to column name using our lookup table
          const apiField = Object.keys(fieldToColumnMap).find(
            key => fieldToColumnMap[key] === column
          );
          
          if (apiField && deal[apiField] !== undefined) {
            formattedDeal[column] = deal[apiField];
          } else if (column === 'Time' && deal['TimeCreate'] !== undefined) {
            // Special case for Time field which might be in TimeCreate
            formattedDeal[column] = deal['TimeCreate'];
          } else {
            formattedDeal[column] = '';
          }
        }
      });
      
      processedData.push(formattedDeal);
    }
    

    if (endIndex < totalDeals) {
      setTimeout(() => processBatch(endIndex), 0);
    } else {
      // All data processed, generate and download CSV
      const csvData = convertJsonToCsv(processedData);
      downloadFile({
        data: csvData,
        fileName: `${fileName}.csv`,
        fileType: 'text/csv'
      });
    }
  };
  
  // Start processing the first batch
  processBatch(0);
}

const captions = [
  "Deal",
  "Order",
  "Position",
  "Login",
  "Name",
  "Time",
  "Type",
  "Entry",
  "Symbol",
  "Volume",
  "Price",
  "S / L",
  "T / P",
  "Reason",
  "Commission",
  "Fee",
  "Profit",
  "Dealer",
  "Comment",
]

// Define the type for the deal array items
type DealItemArray = string | number | null;

const MostProfitablePage = () => {
  const [isSetForcedUpdate, setIsForcedUpdate] = useState(false)
  const [currentPage, setCurrentpage] = useState(1)
  const [isDownloading, setIsDownloading] = useState(false)
  const toast = useToast()
  const [[startDate, endDate], setDateRange] = useState<
    [Date | undefined, Date | undefined]
  >([undefined, undefined])

  const [loginId, setLoginId] = useState("")
  const [symbolStr, setSymbolStr] = useState<string>("")
  const [amt, setAmt] = useState("0")
  const [finalState, setFinalState] = useState<{
    amt: string
    loginId: string
    symbolStr: string
    startDate: Date | undefined
    endDate: Date | undefined
  }>({
    loginId: loginId,
    symbolStr: symbolStr,
    startDate: startDate,
    endDate: endDate,
    amt: amt,
  })

  const updateFinalState = () => {
    setFinalState({
      loginId,
      symbolStr,
      startDate,
      endDate,
      amt,
    })
  }

  const {
    data,
    isLoading,
    isError,
    isRefetching,
    isFetched,
    isFetchedAfterMount,
  } = useQuery(
    [
      "profit",
      {
        currentPage,
        finalState,
      },
    ],
    () =>
      fetchProfitLoss(
        false,
        currentPage,
        finalState?.startDate ? formatDate(finalState?.startDate) : "",
        finalState?.endDate
          ? formatDate(finalState?.endDate)
          : finalState?.startDate
          ? formatDate(finalState?.startDate)
          : "",
        finalState?.loginId,
        finalState.symbolStr,
        finalState.amt
      ),
    {
      refetchInterval: 4000,
    }
  )

  useEffect(() => {
    if (isFetchedAfterMount) {
      setIsForcedUpdate(true)
    }
  }, [currentPage])

  useEffect(() => {
    if (!isFetchedAfterMount) {
      if (!isRefetching) {
        if (isSetForcedUpdate) {
          setIsForcedUpdate(false)
        }
      }
    }
  }, [isRefetching])

  // Optimized download function that fetches all data at once
  const downloadCSV = async () => {
    try {
      setIsDownloading(true);
      
      // Fetch all data for CSV with the current filter parameters
      const response = await fetchAllProfitLossForCSV(
        false, 
        finalState?.startDate ? formatDate(finalState?.startDate) : "",
        finalState?.endDate
          ? formatDate(finalState.endDate)
          : finalState?.startDate
          ? formatDate(finalState?.startDate)
          : "",
        finalState?.loginId,
        finalState.symbolStr,
        finalState.amt
      );
      
      // Check if we have deals data
      if (!response?.data?.deals || response.data.deals.length === 0) {
        toast({
          title: "No data available",
          status: "info",
          duration: 3000,
          isClosable: true,
        });
        setIsDownloading(false);
        return;
      }

      downloadProfitLossToCSV(
        response,
        captions,
        `profit-deals-${formatDate(finalState.startDate || new Date())}`
      );
      
      toast({
        title: "Download successful",
        description: `${response.data.deals.length} records downloaded`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error downloading CSV:", error);
      toast({
        title: "Download failed",
        description: "There was an error downloading the CSV file",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <CGrid
        templateColumns={[
          "repeat(1, 1fr)",
          "repeat(2, 1fr)",
          "repeat(3, 1fr)",
          "repeat(4, 1fr)",
          "repeat(5, 1fr)",
        ]}
        gap={6}
      >
        <Box position="relative">
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            setDateRange={setDateRange}
          />
        </Box>

        <Box my={4} width="100%">
          <InputWrapper title="Enter Login ID">
            <Input
              value={loginId}
              onChange={(e) => setLoginId(e?.target?.value)}
            />
          </InputWrapper>
        </Box>

        <Box my={4} width="100%">
          <SelectSymbols
            selectedSymbol={symbolStr ?? "ALL"}
            onOptionChange={(_option: string) => {
              setSymbolStr(_option)
            }}
          />
        </Box>

        <Box my={4} width="100%">
          <InputWrapper title="Profit more then">
            <Input
              type="number"
              textAlign="right"
              value={amt ?? "0"}
              onChange={(e) => setAmt(e?.target?.value)}
            />
          </InputWrapper>
        </Box>

        <Box>
          <Flex align="center">
            <Box pt={[0, "35px"]} pr={4}>
              <Button
                colorScheme="blue"
                onClick={() => {
                  updateFinalState()
                }}
              >
                <Box as={AiOutlineSearch} mr={2} />
                Search
              </Button>
            </Box>
            <Box pt={[0, "35px"]}>
              <Button
                leftIcon={<AiOutlineDownload />}
                colorScheme="green"
                isDisabled={isLoading || isDownloading}
                isLoading={isDownloading}
                loadingText="Downloading..."
                onClick={downloadCSV}
              >
                Download CSV
              </Button>
            </Box>
          </Flex>
        </Box>
      </CGrid>

      <Box>
        <Grid>
          {isLoading ? (
            <Tr>
              <Td colSpan={captions?.length}>
                <BlockedLoader isBlocked={isLoading}>
                  <></>
                </BlockedLoader>
              </Td>
            </Tr>
          ) : (
            <>
              {data?.deals?.data?.length ? (
                <>
                  {_.map(data?.deals?.data ?? [], (deal) => {
                    return <GridRow key={deal} deal={deal} />
                  })}{" "}
                </>
              ) : (
                <>
                  <Tr>
                    <Td colSpan={captions?.length}>
                      <Box p={4} textAlign="center">
                        <Text color="text.regular">
                          No data found for the selected dates
                        </Text>
                      </Box>
                    </Td>
                  </Tr>
                </>
              )}
            </>
          )}
        </Grid>
      </Box>

      <Box my={4}>
        <Paginator
          currentPage={currentPage}
          totalPages={data?.deals?.last_page ?? 1}
          onChangePage={(_p: number) => setCurrentpage(_p)}
        />
      </Box>
    </>
  )
}

export default MostProfitablePage

const Grid = ({ children }: { children: React.ReactNode }) => {
  return (
    <>
      <Box overflowX="auto">
        <Table variant="striped" color="text.regular" border={1}>
          <Thead>
            <Tr my=".8rem" ps="0px">
              {captions.map((caption, idx) => {
                return (
                  <Th
                    color="gray.400"
                    key={idx}
                    ps={idx === 0 ? "0px" : "0px"}
                    textAlign={caption === "PL" ? "right" : "unset"}
                  >
                    {caption}
                  </Th>
                )
              })}
            </Tr>
          </Thead>
          <Tbody>{children}</Tbody>
        </Table>
      </Box>
    </>
  )
}

const GridRow = ({ deal }: { deal: any }) => {
  const renderPofitLoss = (_amount: number) => {
    return (
      <Text
        color={_amount < 0 ? "red" : "green"}
        fontSize="md"
        fontWeight="bold"
        minWidth="100%"
        textAlign="right"
      >
        {_amount}
      </Text>
    )
  }

  return (
    <>
      <Tr>
        {_.map(deal, (_d, index) => {
          if (deal?.length === index + 1) return <></>

          return (
            <Td pl={1} pr={2} key={index}>
              <Flex align="center" py=".8rem" minWidth="100%" flexWrap="nowrap">
                <Text fontSize="md" fontWeight="bold" minWidth="100%">
                  {_d}
                </Text>
              </Flex>
            </Td>
          )
        })}
      </Tr>
    </>
  )
}